import React, { Component } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import GridLayout, { noCompactor } from "react-grid-layout";
import { GoPlus, GoInfo } from "react-icons/go";
import { GrRevert } from "react-icons/gr";
import { TfiSave } from "react-icons/tfi";
import { BiExpandHorizontal, BiCollapseHorizontal } from "react-icons/bi";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import Tooltip from "../../../controls/common/tooltip";
import LibraryPane from "../../../controls/library/index";
import { removeComponentById } from "../../widgets/common/widgetBase";
import DetailsPane from "../../details/index";

import { findFreeGridCell } from "../../../controls/library/placement";
import GridConverter from "./converter";
import ChatNode from "../../widgets/chat/index";
import GraphNode from "../../widgets/graph/index";
import HtmlNode from "../../widgets/html/index";
import MessageNode from "../../widgets/message/index";
import MessagesNode from "../../widgets/messages/index";
import ThreadNode from "../../widgets/thread/index";
import TradingViewNode from "../../widgets/tradingview/index";
import MissionsNode from "../../widgets/missionControl/missions/index";
import "./grid.css";

const WIDGET_BY_TYPE = {
  "widget.chat": ChatNode,
  "widget.graph": GraphNode,
  "widget.html": HtmlNode,
  "widget.message": MessageNode,
  "widget.messages": MessagesNode,
  "widget.thread": ThreadNode,
  "widget.tradingview": TradingViewNode,
  "system.work.missions": MissionsNode,
};

const DEFAULT_DROPPING_ITEM = { i: "__dropping__", w: 4, h: 4 };

export default class GridWorkspace extends Component {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.state = {
      active: false,
      containerWidth: 1200,
      // Measured visible height of .grid-canvas; drives how many full rows
      // fit. Default matches the canvas CSS min-height for a sane first paint.
      containerHeight: 600,
      droppingItem: DEFAULT_DROPPING_ITEM,
      // True while a drag or resize gesture is in progress. Used to freeze
      // geometry in "fit" mode so the row height does not rescale mid-gesture.
      interacting: false,
    };
    // Cached geometry from the last render; used to freeze geometry during
    // an active drag/resize (interacting === true) in fit/fixed_rows modes.
    this.lastGeometry = null;
    this.converter = new GridConverter(props.shell.shellInterface);
  }

  componentDidMount() {
    this.measureContainer();
    window.addEventListener("resize", this.measureContainer);

    // Safety net: if react-grid-layout ever misses an onDragStop/onResizeStop
    // (e.g. the pointer is released outside the window), a window-level mouseup
    // still clears the interaction flag so "fit" geometry doesn't stay frozen.
    window.addEventListener("mouseup", this.handleGlobalPointerUp);

    // Library/details pane toggles change the canvas width without firing a
    // window resize. Observe the canvas itself so the grid re-measures on
    // any layout-driven size change.
    if (typeof ResizeObserver !== "undefined" && this.containerRef.current) {
      this.containerObserver = new ResizeObserver(() => this.measureContainer());
      this.containerObserver.observe(this.containerRef.current);
    }

    this.handleSidebarDragStart = (ev) => {
      const componentDef = ev?.detail?.componentDef;
      if (!componentDef) return;
      const layout = this.resolveLayout();
      if (!layout) return;
      const gridGeometry = {
        containerWidth: this.containerWidth(),
        columns: layout.columns,
        rowHeight: this.effectiveGridGeometry(layout).rowHeight,
        margin: layout.margin || [10, 10],
        containerPadding: layout.container_padding || [10, 10],
      };
      const { w, h } = this.converter.getComponentGridSize(
        componentDef, this.props.shell.manifest, gridGeometry,
      );
      this.setState({ droppingItem: { i: "__dropping__", w, h } });
    };
    document.addEventListener("playground-widget-drag-start", this.handleSidebarDragStart);

    // Escape clears the current selection; Delete/Backspace removes the selected widget.
    //
    // Delete is guarded so it can't repeat the failure that originally kept it unbound: a Delete
    // pressed inside a nested widget (e.g. editing a step in the Mission Control workflow editor)
    // used to take the whole widget with it. The key only removes the widget when focus is NOT inside
    // it — select a widget by clicking its frame and Delete removes it; click into its content first
    // and the key belongs to that content. Mirrors the free-form canvas handler.
    this.handleKeyDown = (event) => {
      // Skip when the user is typing — these shortcuts are widget-level, not text-input-level.
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || event.target?.isContentEditable) return;

      const shellInterface = this.props.shell?.shellInterface;
      if (!shellInterface) return;

      if (event.key === "Escape" && shellInterface.updateDetails) {
        shellInterface.updateDetails(null, null);
        return;
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        if (!shellInterface.canEditRevision?.()) return;
        const details = this.props.shell?.details;
        if (details?.view !== "component" || !details?.data?.id) return;
        if (this.isFocusInsideComponent(details.data.id)) return;
        event.preventDefault();
        removeComponentById(shellInterface, details.data.id);
      }
    };
    document.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.measureContainer);
    window.removeEventListener("mouseup", this.handleGlobalPointerUp);
    document.removeEventListener("playground-widget-drag-start", this.handleSidebarDragStart);
    document.removeEventListener("keydown", this.handleKeyDown);
    if (this.containerObserver) {
      this.containerObserver.disconnect();
      this.containerObserver = null;
    }
  }

  measureContainer = () => {
    if (!this.containerRef.current) return;
    const nextWidth = this.containerRef.current.clientWidth;
    const nextHeight = this.containerRef.current.clientHeight;
    // Skip zero-size measurements (display: none on the playground tab
    // makes clientWidth/clientHeight 0). Without this, ResizeObserver fires
    // twice on every tab switch — 0 on hide, N on show — and the grid
    // reflows on return even though nothing visually changed.
    if (nextWidth <= 0 || nextHeight <= 0) return;
    if (nextWidth === this.state.containerWidth && nextHeight === this.state.containerHeight) return;
    this.setState({ containerWidth: nextWidth, containerHeight: nextHeight });
  };

  containerWidth() {
    // The canvas no longer carries CSS padding — react-grid-layout handles
    // its own containerPadding via gridConfig — so we hand the full
    // measured client width straight through. The library positions the
    // first cell at containerPadding[0], where our gridlines start too.
    return Math.max(0, this.state.containerWidth || 1200);
  }

  /**
   * Returns the layout block from the loaded definition JSON. The DDB
   * metadata record only carries layout on the create response, so for
   * any reloaded playground we have to read from the JSON backup.
   */
  resolveLayout() {
    return this.props.shell?.definitionObjectBackup?.layout
      ?? this.props.shell?.definition?.layout
      ?? null;
  }

  canEdit() {
    return !!this.props.shell?.shellInterface?.canEditRevision?.();
  }

  handleLayoutChange = (nextLayoutItems) => {
    if (!this.canEdit()) return;
    const byId = new Map(nextLayoutItems.map((it) => [it.i, it]));

    const changed = this.props.shell.nodes.some((node) => {
      const next = byId.get(node.id);
      return next && (
        next.x !== node.layout.x || next.y !== node.layout.y ||
        next.w !== node.layout.w || next.h !== node.layout.h
      );
    });
    if (!changed) return;

    const updatedNodes = this.props.shell.nodes.map((node) => {
      const nextItem = byId.get(node.id);
      if (!nextItem) return node;
      return { ...node, layout: { ...node.layout, ...nextItem } };
    });

    this.props.shell.workflowInterface.setNodes(updatedNodes, /*isModified=*/true);
  };

  handleInteractionStart = () => this.setState({ active: true, interacting: true });

  // Clearing interacting triggers a re-render so geometry recomputes from
  // the committed layout (e.g. "fit" snaps to the new content bounds).
  handleInteractionEnd = () => this.setState({ active: false, interacting: false });

  // Only acts if an interaction is still flagged, so normal clicks are a no-op.
  handleGlobalPointerUp = () => {
    if (this.state.interacting) this.handleInteractionEnd();
  };

  handleDrop = (_layout, layoutItem, event) => {
    if (!this.canEdit()) return;
    // The library panel serializes the full component definition as JSON
    // under the "text/plain" key on dragstart.
    const raw = event.dataTransfer?.getData("text/plain");
    if (!raw) return;
    let componentDef;
    try {
      componentDef = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!componentDef || !componentDef.type) return;

    const newNode = this.converter.createNewNode(
      componentDef,
      { x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h },
      this.props.shell.manifest,
    );

    const nextNodes = [...this.props.shell.nodes, newNode];
    this.props.shell.workflowInterface.setNodes(nextNodes, /*isModified=*/true);
  };

  handleAddComponent = (componentDef) => {
    if (!this.canEdit()) return;
    const layout = this.resolveLayout();
    if (!layout) return;

    const gridGeometry = {
      containerWidth: this.containerWidth(),
      columns: layout.columns,
      rowHeight: this.effectiveGridGeometry(layout).rowHeight,
      margin: layout.margin || [10, 10],
      containerPadding: layout.container_padding || [10, 10],
    };
    const { w, h } = this.converter.getComponentGridSize(
      componentDef, this.props.shell.manifest, gridGeometry,
    );
    const width = Math.min(w, layout.columns);
    const layoutItems = this.props.shell.nodes.map((n) => n.layout);
    const { x, y } = findFreeGridCell(layoutItems, width, h, layout.columns);

    const newNode = this.converter.createNewNode(
      componentDef, { x, y, w: width, h }, this.props.shell.manifest,
    );
    const nextNodes = [...this.props.shell.nodes, newNode];
    this.props.shell.workflowInterface.setNodes(nextNodes, /*isModified=*/true);
  };

  // HTML5 drag-and-drop fallback on the canvas itself. react-grid-layout's
  // internal isDroppable handling can miss drops when the grid is empty
  // (its inner div collapses) or when the cursor is over canvas padding,
  // so we accept drops on the wrapper and compute the grid cell ourselves.
  handleCanvasDragOver = (event) => {
    if (!this.canEdit()) return;
    if (!event.dataTransfer) return;
    if (Array.from(event.dataTransfer.types || []).includes("text/plain")) {
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
    }
  };

  handleCanvasDrop = (event) => {
    if (!this.canEdit()) return;
    const raw = event.dataTransfer?.getData("text/plain");
    if (!raw) return;
    event.preventDefault();
    let componentDef;
    try {
      componentDef = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!componentDef || !componentDef.type) return;

    const layout = this.resolveLayout();
    if (!layout) return;

    const canvas = this.containerRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const offsetX = event.clientX - rect.left + canvas.scrollLeft;
    const offsetY = event.clientY - rect.top + canvas.scrollTop;

    const margin = layout.margin || [10, 10];
    const containerPadding = layout.container_padding || [10, 10];
    const innerWidth = (this.state.containerWidth || rect.width)
      - 2 * containerPadding[0]
      - margin[0] * (layout.columns - 1);
    const cellWidth = innerWidth > 0 ? innerWidth / layout.columns : 0;
    const stepX = cellWidth + margin[0];
    const stepY = this.effectiveGridGeometry(layout).rowHeight + margin[1];

    const xCells = stepX > 0
      ? Math.max(0, Math.floor((offsetX - containerPadding[0]) / stepX))
      : 0;
    const yCells = stepY > 0
      ? Math.max(0, Math.floor((offsetY - containerPadding[1]) / stepY))
      : 0;

    const dropping = this.state.droppingItem || DEFAULT_DROPPING_ITEM;
    const w = Math.min(dropping.w || 4, layout.columns);
    const h = dropping.h || 4;
    const x = Math.min(xCells, Math.max(0, layout.columns - w));
    const y = yCells;

    const newNode = this.converter.createNewNode(
      componentDef,
      { x, y, w, h },
      this.props.shell.manifest,
    );

    const nextNodes = [...this.props.shell.nodes, newNode];
    this.props.shell.workflowInterface.setNodes(nextNodes, /*isModified=*/true);
  };

  // Live shell value takes priority over the persisted layout block; falls back to "pitch".
  resolveVerticalSizing(layout) {
    return this.props.shell?.verticalSizing ?? layout?.vertical_sizing ?? "pitch";
  }

  // Live shell value takes priority over the persisted layout block; clamped to 1..128.
  resolveReferenceRows(layout) {
    const v = this.props.shell?.referenceRows ?? layout?.reference_rows ?? 12;
    return Math.max(1, Math.min(128, parseInt(v, 10) || 12));
  }

  // The 1-based row index of the bottom edge of the lowest widget (0 when grid is empty).
  contentMaxRow() {
    const nodes = this.props.shell?.nodes || [];
    let maxRow = 0;
    for (const n of nodes) {
      const y = (n.layout && n.layout.y) || 0;
      const h = (n.layout && n.layout.h) || 0;
      if (y + h > maxRow) maxRow = y + h;
    }
    return maxRow; // 0 when empty
  }

  // Derive the effective row count and row height for the current sizing mode.
  // "pitch"      — treat layout.row_height as a target pitch; derive row count so a whole
  //                number of full rows exactly fills the visible canvas (unchanged behavior).
  // "fit"        — row count = content max row (bottom edge of lowest widget); row height
  //                derived from canvas height. Empty grid falls back to reference_rows.
  // "fixed_rows" — row count = reference_rows (clamped 1..128); row height derived from
  //                canvas height.
  // rowHeight is kept fractional on purpose — rounding would reintroduce partial-row drift.
  // Returns { rows, rowHeight }.
  effectiveGridGeometry(layout) {
    const padY = (layout.container_padding || [10, 10])[1];
    const marginY = (layout.margin || [10, 10])[1];
    const H = this.state.containerHeight || 600;
    const usableH = Math.max(0, H - 2 * padY);

    const mode = this.resolveVerticalSizing(layout);
    let rows;
    if (mode === "fit") {
      const maxRow = this.contentMaxRow();
      rows = Math.max(1, maxRow || this.resolveReferenceRows(layout)); // empty grid → reference_rows
    } else if (mode === "fixed_rows") {
      rows = this.resolveReferenceRows(layout);
    } else { // "pitch" (default) — unchanged from today
      const target = layout.row_height || 30;
      rows = Math.max(1, Math.round((usableH + marginY) / (target + marginY)));
    }
    const rowHeight = Math.max(1, (usableH - (rows - 1) * marginY) / rows);
    return { rows, rowHeight };
  }

  gridlineBackgroundStyle(layout, rowHeight) {
    const margin = layout.margin || [10, 10];
    const containerPadding = layout.container_padding || [10, 10];
    // Inner content width = canvas width minus the CSS padding we apply to
    // .grid-canvas (which equals containerPadding). With no extra padding
    // from react-grid-layout (we pass [0, 0] in gridConfig), the cell width
    // is just (innerWidth - margin*(cols-1)) / cols.
    const innerWidth = Math.max(0, (this.state.containerWidth || 0) - 2 * containerPadding[0]);
    const cellWidth = innerWidth > 0
      ? (innerWidth - margin[0] * (layout.columns - 1)) / layout.columns
      : 0;
    const stepX = cellWidth + margin[0];
    const stepY = rowHeight + margin[1];
    // Place each gridline at the center of a gutter. Both adjacent widget
    // edges (right of col k and left of col k+1) touch the same single
    // line, so widgets read as aligned on all sides without doubled stripes.
    const lineX = containerPadding[0] + cellWidth + margin[0] / 2;
    const lineY = containerPadding[1] + rowHeight + margin[1] / 2;
    return {
      "--grid-step-x": `${stepX}px`,
      "--grid-step-y": `${stepY}px`,
      "--grid-line-x": `${lineX}px`,
      "--grid-line-y": `${lineY}px`,
      "--grid-pad-x": `${containerPadding[0]}px`,
      "--grid-pad-y": `${containerPadding[1]}px`,
    };
  }

  handleWidgetCellClick = (node) => (event) => {
    // Avoid hijacking interactions inside the widget body (inputs, buttons,
    // scroll, etc.). Selection happens when the click lands on the cell
    // wrapper or the drag handle.
    if (event.target.closest(".grid-widget-body")) return;
    this.props.shell.shellInterface.updateDetails("component", node);
  };

  handleCanvasClick = (event) => {
    // Click on empty grid space (not on any widget cell) clears selection.
    // Bubbling lets a widget cell click fire its own handler first; this
    // canvas handler then no-ops because the target is inside the cell.
    if (event.target.closest(".grid-widget-cell")) return;
    // Keep the widget selected (and its header visible) while focus is inside a widget or a widget-spawned
    // modal — typing in a widget's own controls must not clear the selection.
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if (active && active.closest && (active.closest(".grid-widget-cell") || active.closest(".modal"))) {
      return;
    }
    const shellInterface = this.props.shell?.shellInterface;
    if (shellInterface?.updateDetails) {
      shellInterface.updateDetails(null, null);
    }
  };

  isCellSelected(node) {
    const details = this.props.shell.details;
    return details?.view === "component" && details?.data?.id === node.id;
  }

  renderWidgetCell(node, idx) {
    const Widget = WIDGET_BY_TYPE[node.type];
    if (!Widget) return null;
    const selectedClass = this.isCellSelected(node) ? " grid-widget-selected" : "";
    // Spread shell.zoom into data so the widget receives it as a prop and
    // re-renders when the zoom slider moves; the layout itself stays at 1x.
    const data = { ...node.data, zoom: this.props.shell.zoom ?? 1 };
    return (
      <div
        key={node.id}
        data-component-id={node.id}
        className={"grid-widget-cell" + selectedClass}
        style={{ zIndex: idx }}
        onClick={this.handleWidgetCellClick(node)}
      >
        <Widget data={data} />
      </div>
    );
  }

  // Whether focus currently sits inside the given widget's cell — the guard that keeps the Delete
  // shortcut from stealing the key from a nested editor. Mirrors the free-form canvas's
  // isFocusInsideSelectedComponent(); a widget-spawned modal portals to <body>, so it counts too.
  isFocusInsideComponent(componentId) {
    const active = typeof document !== "undefined" ? document.activeElement : null;
    if (!active || !active.closest) return false;
    if (active.closest(".modal")) return true;
    const escaped = (typeof CSS !== "undefined" && CSS.escape) ? CSS.escape(componentId) : componentId;
    return !!active.closest(`[data-component-id="${escaped}"]`);
  }

  render() {
    const layout = this.resolveLayout();
    if (!layout || layout.type !== "grid") return null;

    // Freeze geometry during an active drag/resize so "fit" mode does not
    // rescale the row height mid-gesture (which would shift widgets under
    // the cursor). On interaction end the flag clears, causing a re-render
    // that recomputes geometry from the committed layout positions.
    let effectiveRowHeight;
    if (this.state.interacting && this.lastGeometry) {
      effectiveRowHeight = this.lastGeometry.rowHeight;
    } else {
      const geom = this.effectiveGridGeometry(layout);
      this.lastGeometry = geom;
      effectiveRowHeight = geom.rowHeight;
    }

    const gridNodes = this.props.shell.nodes;
    const layoutItems = gridNodes.map((n) => n.layout);
    const isEmpty = gridNodes.length === 0;

    const containerContentClass
      = this.props.shell.wideContent || this.props.shell.library.visible || this.props.shell.details.visible
      ? "content-row-wide"
      : "content-row";

    // Stay mounted when the tab isn't shown so embedded widgets
    // (TradingView, etc.) keep their internal state across tab switches.
    // Free-form behaves the same way; only the early return that used to
    // guard this branch caused grid widgets to remount.
    const containerStyle = this.props.shown ? undefined : { display: "none" };

    return (
      <Container className={"blob-header-row " + containerContentClass} style={containerStyle}>
        <Row>
          <Col sm={3} className={`detail-panel-col ${this.props.shell.library.visible ? "" : "hidden"}`}>
            <LibraryPane shell={this.props.shell} onAddComponent={this.handleAddComponent} />
          </Col>

          <Col>
            <Card>
              <Card.Body>
                <div className="workflow-controls-pane-left">
                  {!this.props.shell.library.visible &&
                    <Button variant="light" onClick={() => {
                      this.props.shell.shellInterface.updatePane("library", true);
                    }}><GoPlus /></Button>
                  }
                </div>

                <div className="workflow-controls-pane-right">
                  {this.props.shell.modified &&
                    <>
                      <Tooltip text="Revert all changes">
                        <Button variant="light" onClick={() => {
                          this.props.shell.shellInterface.revertPlayground();
                        }}><GrRevert /></Button>
                      </Tooltip>
                      &nbsp;&nbsp;
                      <Tooltip text="Save changes">
                        <Button variant="light" onClick={() => {
                          this.props.shell.shellInterface.savePlayground();
                        }}><TfiSave /></Button>
                      </Tooltip>
                      &nbsp;&nbsp;
                    </>
                  }

                  <Tooltip text="Toggle width">
                    <Button variant="light" onClick={() => {
                      this.props.shell.shellInterface.toggleWideContent();
                    }}>
                      {this.props.shell.wideContent ? <BiCollapseHorizontal /> : <BiExpandHorizontal />}
                    </Button>
                  </Tooltip>

                  {!this.props.shell.details.visible &&
                    <>
                      &nbsp;&nbsp;
                      <Tooltip text="Show details">
                        <Button variant="light" onClick={() => {
                          this.props.shell.shellInterface.updatePane("details", true);
                        }}><GoInfo /></Button>
                      </Tooltip>
                    </>
                  }
                </div>

                <div
                  ref={this.containerRef}
                  className="grid-canvas"
                  onClick={this.handleCanvasClick}
                  onDragOver={this.handleCanvasDragOver}
                  onDrop={this.handleCanvasDrop}
                >
                <div
                  className="grid-canvas-inner"
                  style={this.gridlineBackgroundStyle(layout, effectiveRowHeight)}
                >
                  <GridLayout
                    className="grid-layout"
                    width={this.containerWidth()}
                    layout={layoutItems}
                    gridConfig={{
                      cols: layout.columns,
                      rowHeight: effectiveRowHeight,
                      margin: layout.margin || [10, 10],
                      // react-grid-layout owns the container padding so the
                      // first cell lines up exactly with our gridline pattern
                      // (which is offset by --grid-pad-x / --grid-pad-y).
                      containerPadding: layout.container_padding || [10, 10],
                    }}
                    dragConfig={{
                      enabled: this.canEdit(),
                      handle: ".card-header",
                      cancel: ".btn",
                    }}
                    resizeConfig={{
                      enabled: this.canEdit(),
                      handles: ["se"],
                    }}
                    dropConfig={{
                      enabled: this.canEdit(),
                      defaultItem: this.state.droppingItem,
                    }}
                    compactor={noCompactor}
                    constraints={[]}
                    droppingItem={this.state.droppingItem}
                    onLayoutChange={this.handleLayoutChange}
                    onDrop={this.handleDrop}
                    onDragStart={this.handleInteractionStart}
                    onDragStop={this.handleInteractionEnd}
                    onResizeStart={this.handleInteractionStart}
                    onResizeStop={this.handleInteractionEnd}
                  >
                    {gridNodes.map((node, idx) => this.renderWidgetCell(node, idx))}
                  </GridLayout>
                  {isEmpty && (
                    <div className="grid-empty-hint">
                      Drag a widget from the sidebar to start
                    </div>
                  )}
                </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm={3} className={`detail-panel-col ${this.props.shell.details.visible ? "" : "hidden"}`}>
            <DetailsPane shell={this.props.shell} timezone={this.props.timezone} />
          </Col>
        </Row>
      </Container>
    );
  }
}
